// ─────────────────────────────────────────────────────────────────────────────
// USAGE: Import whichever page you need in your router
//   import { TermsPage, PrivacyPage, CancellationPage } from "./pages/LegalPages";
//
//   <Route path="/terms"        element={<TermsPage />} />
//   <Route path="/privacy"      element={<PrivacyPage />} />
//   <Route path="/cancellation" element={<CancellationPage />} />
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from "react";
import SEO from "../components/SEO";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FaWhatsapp } from "react-icons/fa";

const C = {
  bg:"#f8f9fd", dark:"#1a1a2e", gold:"#f5c518", goldD:"#d4a80f",
  gray:"#6b7280", grayL:"#9ca3af", border:"#e5e7eb", white:"#ffffff",
};
const F = { display:"'Playfair Display', serif", body:"'Plus Jakarta Sans', sans-serif" };

const SHARED_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

  @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }

  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Plus Jakarta Sans',sans-serif; overflow-x:hidden; }

  /* Scroll progress bar */
  .scroll-progress {
    position:fixed; top:0; left:0; height:3px;
    background:linear-gradient(90deg,#f5c518,#d4a80f);
    z-index:9999; transition:width .08s linear;
    pointer-events:none;
  }

  /* TOC Sidebar */
  .toc-sidebar {
    width:210px; flex-shrink:0;
    position:sticky; top:88px;
    background:#fff; border-radius:18px;
    border:1.5px solid #e5e7eb;
    padding:18px 12px;
    box-shadow:0 4px 18px rgba(0,0,0,.06);
    max-height:calc(100vh - 110px);
    overflow-y:auto;
  }
  .toc-sidebar::-webkit-scrollbar { width:4px; }
  .toc-sidebar::-webkit-scrollbar-thumb { background:#e5e7eb; border-radius:4px; }

  .toc-link {
    display:block; padding:8px 10px; border-radius:9px;
    font-size:12.5px; font-weight:500; color:#6b7280;
    text-decoration:none; transition:all .2s;
    border-left:2px solid transparent;
    line-height:1.4; margin-bottom:2px;
  }
  .toc-link:hover { color:#1a1a2e; background:rgba(245,197,24,.08); border-left-color:#f5c518; }
  .toc-link.active { color:#1a1a2e; background:rgba(245,197,24,.1); border-left-color:#f5c518; font-weight:700; }

  /* Section blocks */
  .section-block {
    background:#fff; border-radius:20px;
    border:1.5px solid #e5e7eb;
    padding:28px 32px; margin-bottom:18px;
    box-shadow:0 4px 16px rgba(0,0,0,.05);
    transition:border-color .25s;
    scroll-margin-top:96px;
  }
  .section-block:hover { border-color:rgba(245,197,24,.45); }

  .section-title {
    display:flex; align-items:center; gap:12px; margin-bottom:20px;
  }
  .section-icon {
    width:44px; height:44px; border-radius:12px; flex-shrink:0;
    background:rgba(245,197,24,.1); border:1.5px solid rgba(245,197,24,.25);
    display:flex; align-items:center; justify-content:center; font-size:20px;
  }

  /* Clause rows */
  .clause {
    display:flex; gap:10px; align-items:flex-start;
    background:#f8f9fd; border-radius:11px;
    border:1px solid #e5e7eb; padding:13px 15px;
    margin-bottom:9px; font-size:13.5px; color:#374151;
    line-height:1.75; transition:border-color .2s;
  }
  .clause:hover { border-color:rgba(245,197,24,.4); }
  .clause-num {
    font-size:11px; font-weight:800; color:#d4a80f;
    background:rgba(245,197,24,.12); border:1px solid rgba(245,197,24,.25);
    border-radius:6px; padding:2px 7px; flex-shrink:0;
    margin-top:2px; letter-spacing:.3px;
  }

  /* Callout boxes */
  .box-gold    { background:rgba(245,197,24,.07); border:1px solid rgba(245,197,24,.28); border-radius:13px; padding:14px 16px; margin:14px 0; font-size:13px; color:#92400e; line-height:1.7; }
  .box-red     { background:rgba(239,68,68,.05);  border:1px solid rgba(239,68,68,.2);   border-radius:13px; padding:14px 16px; margin:14px 0; font-size:13px; color:#991b1b; line-height:1.7; }
  .box-blue    { background:rgba(59,130,246,.05); border:1px solid rgba(59,130,246,.2);  border-radius:13px; padding:14px 16px; margin:14px 0; font-size:13px; color:#1e40af; line-height:1.7; }
  .box-green   { background:rgba(34,197,94,.05);  border:1px solid rgba(34,197,94,.2);   border-radius:13px; padding:14px 16px; margin:14px 0; font-size:13px; color:#14532d; line-height:1.7; }

  /* Fee table */
  .fee-table { width:100%; border-collapse:collapse; margin:12px 0; }
  .fee-table th { background:#1a1a2e; color:#f5c518; font-size:12px; font-weight:700; padding:10px 14px; text-align:left; }
  .fee-table td { padding:10px 14px; font-size:13px; color:#374151; border-bottom:1px solid #e5e7eb; }
  .fee-table tr:last-child td { border-bottom:none; }
  .fee-table tr:nth-child(even) td { background:#f8f9fd; }

  /* Contact card */
  .cta-card {
    background:linear-gradient(135deg,#1a1a2e,#2d3a6b);
    border-radius:20px; padding:28px 28px; text-align:center; margin-top:8px;
  }

  @media(max-width:900px) {
    .page-layout   { flex-direction:column !important; }
    .toc-sidebar   { display:none !important; }
    .section-block { padding:20px 16px !important; }
    .fee-table th, .fee-table td { padding:8px 10px !important; font-size:12px !important; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Shared Layout Component
// ─────────────────────────────────────────────────────────────────────────────
function LegalLayout({ title, updated, badge, intro, tocItems, children }) {
  const [scrollPct, setScrollPct] = useState(0);
  const [activeId,  setActiveId]  = useState(tocItems[0]?.id || "");

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const onScroll = () => {
      const el = document.documentElement;
      setScrollPct((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
      for (const item of tocItems) {
        const node = document.getElementById(item.id);
        if (node) {
          const r = node.getBoundingClientRect();
          if (r.top <= 110 && r.bottom > 110) { setActiveId(item.id); break; }
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <style>{SHARED_CSS}</style>
      <div className="scroll-progress" style={{ width: `${scrollPct}%` }} />
      <div style={{ fontFamily: F.body, background: C.bg, color: C.dark, overflowX: "hidden" }}>
        <Header />

        {/* HERO */}
        <div style={{ background: "linear-gradient(135deg,#1a1a2e 0%,#2d3a6b 100%)", padding: "56px 24px 44px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position:"absolute", top:-60, right:-40, width:220, height:220, borderRadius:"50%", background:"rgba(245,197,24,.06)", pointerEvents:"none" }} />
          <div style={{ position:"absolute", bottom:-50, left:-50, width:180, height:180, borderRadius:"50%", background:"rgba(255,255,255,.03)", pointerEvents:"none" }} />

          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(245,197,24,.12)", border:"1px solid rgba(245,197,24,.3)", borderRadius:50, padding:"5px 14px", fontSize:11, color:C.gold, fontWeight:700, letterSpacing:"1px", textTransform:"uppercase", marginBottom:16 }}>
            <span style={{ width:6, height:6, background:C.gold, borderRadius:"50%", animation:"pulse 1.8s ease-in-out infinite" }} />
            {badge}
          </div>

          <h1 style={{ fontFamily:F.display, fontSize:"clamp(30px,5vw,54px)", fontWeight:900, color:C.white, marginBottom:10, letterSpacing:"-1px", animation:"fadeUp .6s ease both" }}>{title}</h1>
          <p style={{ fontSize:13, color:"rgba(255,255,255,.45)", marginBottom:20 }}>Last updated: {updated}</p>

          {intro && (
            <div style={{ maxWidth:600, margin:"0 auto", background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.1)", borderRadius:14, padding:"14px 18px", fontSize:13.5, color:"rgba(255,255,255,.7)", lineHeight:1.7, textAlign:"left" }}>
              📌 {intro}
            </div>
          )}
        </div>

        {/* BODY */}
        <div style={{ maxWidth:1060, margin:"0 auto", padding:"36px 18px 80px" }}>
          <div className="page-layout" style={{ display:"flex", gap:26, alignItems:"flex-start" }}>

            {/* TOC */}
            {/* <div className="toc-sidebar">
              <div style={{ fontSize:10, fontWeight:800, color:C.grayL, letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:10, padding:"0 4px" }}>On this page</div>
              {tocItems.map(item => (
                <a key={item.id} className={`toc-link${activeId === item.id ? " active" : ""}`}
                  href={`#${item.id}`}
                  onClick={e => { e.preventDefault(); scrollTo(item.id); }}>
                  {item.icon} {item.label}
                </a>
              ))}
            </div> */}

            {/* Content */}
            <div style={{ flex:1, minWidth:0 }}>
              {children}

              {/* Bottom CTA */}
              <div className="cta-card">
                <div style={{ fontSize:32, marginBottom:10 }}>💬</div>
                <h3 style={{ fontFamily:F.display, fontSize:20, fontWeight:800, color:C.white, marginBottom:8 }}>Need Clarification?</h3>
                <p style={{ fontSize:13, color:"rgba(255,255,255,.55)", marginBottom:20 }}>Our team is available 24×7 to answer your questions.</p>
                <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
                  <a href="tel:+919265407669" style={{ textDecoration:"none" }}>
                    <button style={{ background:`linear-gradient(135deg,${C.gold},${C.goldD})`, color:C.dark, border:"none", borderRadius:50, padding:"11px 26px", fontSize:13, fontWeight:700, fontFamily:F.body, cursor:"pointer" }}>📞 Call Us</button>
                  </a>
                  <a href="mailto:halosorath@gmail.com" style={{ textDecoration:"none" }}>
                    <button style={{ background:"rgba(255,255,255,.1)", color:C.white, border:"1px solid rgba(255,255,255,.2)", borderRadius:50, padding:"11px 26px", fontSize:13, fontWeight:700, fontFamily:F.body, cursor:"pointer" }}>✉️ Email</button>
                  </a>
                  <a href="https://wa.me/919265407669" target="_blank" rel="noreferrer" style={{ textDecoration:"none" }}>
                    <button style={{ background:"#25D366", color:C.white, border:"none", borderRadius:50, padding:"11px 26px", fontSize:13, fontWeight:700, fontFamily:F.body, cursor:"pointer" }}>💬 WhatsApp</button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />

       
      </div>
    </>
  );
}

// Clause renderer
function Clause({ num, text }) {
  return (
    <div className="clause">
      <span className="clause-num">{num}</span>
      <span>{text}</span>
    </div>
  );
}

// Section block
function Section({ id, icon, title, children }) {
  return (
    <div id={id} className="section-block">
      <div className="section-title">
        <div className="section-icon">{icon}</div>
        <h2 style={{ fontFamily:F.display, fontSize:"clamp(17px,2.5vw,22px)", fontWeight:800, color:C.dark }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 1 — TERMS OF USE
// ═══════════════════════════════════════════════════════════════════
export function TermsPage() {
  const TOC = [
    { id:"intro", icon:"📘", label:"Introduction" },
    { id:"booking", icon:"🚖", label:"Booking Confirmation" },
    { id:"running", icon:"🛣️", label:"Minimum Daily Running" },
    { id:"advance", icon:"💳", label:"Advance Payment" },
    { id:"cancellation", icon:"❌", label:"Cancellation Policy" },
    { id:"charges", icon:"💰", label:"Extra Charges" },
    { id:"customer", icon:"👤", label:"Customer Responsibility" },
    { id:"rules", icon:"⚠️", label:"Driver & Vehicle Rules" },
    { id:"delays", icon:"⏰", label:"Delays & Limitations" },
    { id:"changes", icon:"📝", label:"Changes to Terms" },
    { id:"contact", icon:"📞", label:"Contact Us" },
  ];

  return (
    
    <LegalLayout
      title="Terms & Conditions"
      updated="May 7, 2026"
      badge="Legal Agreement"
      intro="Welcome to HaloSorath Cab Booking Service. By booking a cab with us, you agree to the following Terms & Conditions."
      tocItems={TOC}
    > <SEO page="terms" />

      <Section id="intro" icon="📘" title="Introduction">
        <div className="clause" style={{ flexDirection:"column", gap:8 }}>
          <p>
            Welcome to <strong>HaloSorath</strong>. If you continue to browse and use this website,
            you are agreeing to comply with and be bound by the following terms and conditions of use.
          </p>

          <p>
            These terms together with our privacy policy govern HaloSorath’s relationship with you
            in relation to this website and cab booking services.
          </p>

          <p>
            If you disagree with any part of these terms and conditions,
            please do not use our website or services.
          </p>
        </div>

        <div className="box-green">
          ✅ By using HaloSorath services, you agree to all booking, payment,
          cancellation, and safety policies mentioned below.
        </div>
      </Section>

      <Section id="booking" icon="🚖" title="1. Booking Confirmation">
        <Clause
          num="1.1"
          text="All bookings are subject to availability."
        />

        <Clause
          num="1.2"
          text="Booking will be confirmed only after receiving the required part payment or advance amount."
        />
      </Section>

      <Section id="running" icon="🛣️" title="2. Minimum Daily Running">
        <Clause
          num="2.1"
          text="The cab must run a minimum of 300 KM per day."
        />

        <Clause
          num="2.2"
          text="If the total journey distance is less than 300 KM in a day, charges will still be calculated based on 300 KM minimum running."
        />

        <div className="box-gold">
          🚗 Minimum billing is calculated on 300 KM per day even if actual travel is lower.
        </div>
      </Section>

      <Section id="advance" icon="💳" title="3. Advance / Part Payment">
        <Clause
          num="3.1"
          text="Customers must pay a part payment or advance amount to confirm the booking."
        />

        <Clause
          num="3.2"
          text="The remaining amount must be paid as agreed before or during the trip."
        />
      </Section>

      <Section id="cancellation" icon="❌" title="4. Cancellation Policy">
        <Clause
          num="4.1"
          text="If the customer cancels the ride after booking confirmation, the advance or part payment will be non-refundable."
        />

        <Clause
          num="4.2"
          text="HaloSorath reserves the right to cancel bookings due to unavoidable circumstances. In such cases, the paid amount may be refunded or adjusted."
        />

        <div className="box-red">
          ⚠️ Advance payment becomes non-refundable after booking confirmation.
        </div>
      </Section>

      <Section id="charges" icon="💰" title="5. Extra Charges">
        <p style={{ fontSize:"13.5px", color:C.gray, lineHeight:1.7, marginBottom:14 }}>
          The following charges are not included in the base fare unless specifically mentioned:
        </p>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            "Toll Tax",
            "Parking Charges",
            "State Tax",
            "Driver Allowance / Night Charges (if applicable)",
          ].map((item, i) => (
            <div key={i} className="clause">
              <span style={{ color:C.goldD, fontWeight:700 }}>₹</span>
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div className="box-blue">
          ℹ️ These charges must be paid by the customer separately.
        </div>
      </Section>

      <Section id="customer" icon="👤" title="6. Customer Responsibility">
        <Clause
          num="6.1"
          text="Customers must provide correct pickup and contact details."
        />

        <Clause
          num="6.2"
          text="Any delay caused by incorrect information may result in additional waiting charges."
        />
      </Section>

      <Section id="rules" icon="⚠️" title="7. Driver & Vehicle Rules">

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

          {[
            "Smoking or consuming intoxicants inside the taxi is strictly prohibited.",
            "The driver may refuse pickup or ask passengers to leave the cab in case of intoxication or misbehavior.",
            "Littering, damaging seats, interiors, or devices inside the taxi is prohibited.",
            "Customers cannot force drivers to break Traffic, RTO, Police, or Government rules.",
            "Maximum 4 adult passengers are allowed per standard taxi booking.",
            "Customers should not overload luggage or use passenger seats for luggage storage.",
            "Extra charges may apply for far-off locations and toll taxes.",
            "HaloSorath is not responsible if passengers miss flights, trains, or buses.",
          ].map((item, i) => (
            <div key={i} className="clause">
              <span
                style={{
                  width:22,
                  height:22,
                  borderRadius:"50%",
                  background:"rgba(239,68,68,.1)",
                  border:"1px solid rgba(239,68,68,.25)",
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  fontSize:11,
                  color:"#dc2626",
                  flexShrink:0,
                  marginTop:1
                }}
              >
                !
              </span>

              <span>{item}</span>
            </div>
          ))}
        </div>

        <div className="box-red">
          🚫 Drivers have the right to refuse service if passengers violate safety,
          legal, or behavioral rules.
        </div>

        <div className="box-blue">
          ℹ️ HaloSorath services are provided on a best-effort basis and may be affected
          by technical, traffic, or operational limitations.
        </div>
      </Section>

      <Section id="delays" icon="⏰" title="8. Delays & Service Limitations">
        <p style={{ fontSize:"13.5px", color:C.gray, lineHeight:1.7, marginBottom:14 }}>
          HaloSorath will try its best to provide timely service, but we are not responsible for delays caused by:
        </p>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            "Traffic",
            "Weather conditions",
            "Roadblocks",
            "Mechanical issues",
            "Government restrictions or emergencies",
          ].map((item, i) => (
            <div key={i} className="clause">
              <span style={{ color:"#2563eb", fontWeight:700 }}>⏱️</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section id="changes" icon="📝" title="9. Changes to Terms">
        <Clause
          num="9.1"
          text="HaloSorath may update these Terms & Conditions at any time without prior notice."
        />
      </Section>

      <Section id="contact" icon="📞" title="10. Contact Us">
        <div className="clause" style={{ flexDirection:"column", gap:10 }}>
          <p>
            For booking support or questions regarding these Terms & Conditions,
            please contact HaloSorath customer support.
          </p>

          <p>
            Thank you for choosing <strong>HaloSorath Cab Booking Service</strong>.
          </p>
        </div>

        <div className="box-green">
          📞 Our support team is available to help you with bookings,
          cancellations, and ride assistance.
        </div>
      </Section>

    </LegalLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 2 — PRIVACY POLICY
// ═══════════════════════════════════════════════════════════════════
export function PrivacyPage() {
  const TOC = [
    { id:"intro", icon:"🔐", label:"Introduction" },
    { id:"collect", icon:"📦", label:"Information We Collect" },
    { id:"usage", icon:"⚙️", label:"How We Use Information" },
    { id:"sharing", icon:"🤝", label:"Information Sharing" },
    { id:"security", icon:"🛡️", label:"Data Security" },
    { id:"promo", icon:"🎉", label:"Promotional Messages" },
    { id:"changes", icon:"📝", label:"Policy Changes" },
    { id:"contact", icon:"📞", label:"Contact Us" },
  ];

  // const COLLECT_ITEMS = [
  //   { icon:"👤", label:"Personal Info",      desc:"Name, email, phone number, login credentials, address, government ID, birth date, and photo." },
  //   { icon:"🚗", label:"Driver/Partner Info", desc:"Vehicle details, insurance information (for driver partners only)." },
  //   { icon:"📍", label:"Location Data",      desc:"GPS, IP address, and WiFi signals to determine your current location for trip matching." },
  //   { icon:"💳", label:"Transaction Info",   desc:"Itinerary, trip date/time, amount charged, distance, promotion codes, and related details." },
  //   { icon:"📊", label:"Log Data",           desc:"Device IP, access times, pages viewed, crashes, browser type, and referring services." },
  //   { icon:"⭐", label:"Feedback & Ratings", desc:"Reviews and ratings from riders, drivers, partners, and third parties." },
  //   { icon:"📋", label:"Survey Data",        desc:"Information relevant to customer surveys and promotional offers." },
  // ];

  // const USAGE_ITEMS = [
  //   "Enable reliable and convenient transportation for all users",
  //   "Enhance the safety and security of our platform and services",
  //   "Provide responsive customer support",
  //   "Research and development to continuously improve our products",
  //   "Enable communications between users (riders and drivers)",
  //   "Provide promotions, contests, and personalized offers",
  //   "Comply with legal proceedings when required by law",
  // ];

  // const COOKIE_USES = [
  //   "Authenticating and keeping users logged in securely",
  //   "Remembering your preferences and settings",
  //   "Determining the popularity and relevance of content",
  //   "Delivering and measuring advertising campaign effectiveness",
  //   "Analyzing site traffic trends and user behavior patterns",
  // ];

  return (
     <LegalLayout
      title="Privacy Policy"
      updated="May 7, 2026"
      badge="Privacy & Data Protection"
      intro="Welcome to HaloSorath. We value your privacy and are committed to protecting your personal information."
      tocItems={TOC}
    >
      <SEO page="privacy" />

      <Section id="intro" icon="🔐" title="Privacy Policy – HaloSorath Cab Booking Service">
        <div className="clause" style={{ flexDirection:"column", gap:8 }}>
          <p>
            Welcome to <strong>HaloSorath</strong>. We value your privacy and are committed to protecting your personal information.
          </p>

          <p>
            This Privacy Policy explains how we collect, use, and protect your information when you use our cab booking services.
          </p>
        </div>

        <div className="box-green">
          ✅ <strong>Your Privacy Matters:</strong> HaloSorath never sells or rents your personal information to third parties.
        </div>
      </Section>

      <Section id="collect" icon="📦" title="1. Information We Collect">
        <p style={{ fontSize:"13.5px", color:C.gray, lineHeight:1.7, marginBottom:14 }}>
          When you use HaloSorath services, we may collect the following information:
        </p>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))", gap:12 }}>
          {[
            {
              icon:"👤",
              title:"Name",
              desc:"Your full name used for booking and communication purposes."
            },
            {
              icon:"📱",
              title:"Mobile Number",
              desc:"Used to contact you regarding your booking and ride updates."
            },
            {
              icon:"📍",
              title:"Journey Pickup Address",
              desc:"Collected to provide accurate cab booking and pickup services."
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                background:"#f8f9fd",
                border:`1px solid ${C.border}`,
                borderRadius:12,
                padding:"14px 16px",
                display:"flex",
                gap:12,
                alignItems:"flex-start"
              }}
            >
              <div
                style={{
                  width:40,
                  height:40,
                  borderRadius:10,
                  background:"rgba(245,197,24,.1)",
                  border:"1px solid rgba(245,197,24,.25)",
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  fontSize:18,
                  flexShrink:0
                }}
              >
                {item.icon}
              </div>

              <div>
                <div style={{ fontWeight:700, fontSize:13, color:C.dark, marginBottom:4 }}>
                  {item.title}
                </div>

                <div style={{ fontSize:12.5, color:C.gray, lineHeight:1.6 }}>
                  {item.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="box-blue">
          ℹ️ We collect this information only to provide our cab booking services efficiently.
        </div>
      </Section>

      <Section id="usage" icon="⚙️" title="2. How We Use Your Information">
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            "Booking and managing your cab rides",
            "Contacting you regarding your booking",
            "Customer support and service communication",
            "Sending festival offers, promotional messages, or special discounts related to HaloSorath services in the future",
          ].map((item, i) => (
            <div key={i} className="clause">
              <span
                style={{
                  width:22,
                  height:22,
                  borderRadius:"50%",
                  background:"rgba(34,197,94,.1)",
                  border:"1px solid rgba(34,197,94,.25)",
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  fontSize:11,
                  color:"#16a34a",
                  flexShrink:0,
                  marginTop:1
                }}
              >
                ✔
              </span>

              <span>{item}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section id="sharing" icon="🤝" title="3. Information Sharing">
        <div className="box-green">
          🔒 <strong>HaloSorath respects your privacy.</strong>
        </div>

        <Clause
          num="3.1"
          text="We do not sell, rent, or share your personal information with any third party."
        />

        <Clause
          num="3.2"
          text="Your information is used only for HaloSorath service-related purposes."
        />
      </Section>

      <Section id="security" icon="🛡️" title="4. Data Security">
        <Clause
          num="4.1"
          text="We take reasonable steps to protect your personal information from unauthorized access, misuse, or disclosure."
        />

        <div className="box-blue">
          🛡️ HaloSorath continuously works to keep your information safe and secure.
        </div>
      </Section>

      <Section id="promo" icon="🎉" title="5. Promotional Messages">
        <p style={{ fontSize:"13.5px", color:C.gray, lineHeight:1.7, marginBottom:14 }}>
          By using our services, you agree that HaloSorath may contact you in the future with:
        </p>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            "Festival offers",
            "Discount coupons",
            "Service updates",
            "Promotional messages related to HaloSorath",
          ].map((item, i) => (
            <div key={i} className="clause">
              <span style={{ color:C.goldD, fontWeight:700 }}>🎁</span>
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div className="box-gold">
          📩 You may request to stop receiving promotional messages at any time.
        </div>
      </Section>

      <Section id="changes" icon="📝" title="6. Changes to This Policy">
        <Clause
          num="6.1"
          text="HaloSorath may update this Privacy Policy from time to time. Any updates will be posted on this page."
        />
      </Section>

      <Section id="contact" icon="📞" title="7. Contact Us">
        <div className="clause" style={{ flexDirection:"column", gap:10 }}>
          <p>
            If there are any questions regarding this privacy policy, you may contact us using the information on the Contact Us page.
          </p>

          <p>
            Our support team is available to assist you regarding privacy and service-related concerns.
          </p>
        </div>

        <div className="box-green">
          📞 Thank you for trusting <strong>HaloSorath Cab Booking Service</strong>.
        </div>
      </Section>

    </LegalLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 3 — CANCELLATION & REFUND POLICY
// ═══════════════════════════════════════════════════════════════════
export function CancellationPage() {
  const TOC = [
    { id:"overview",      icon:"📋", label:"Policy Overview" },
    { id:"cancellation",  icon:"❌", label:"Cancellation Policy" },
    { id:"fees",          icon:"💰", label:"Cancellation Fees" },
    { id:"refund",        icon:"↩️", label:"Refund Policy" },
    { id:"vouchers",      icon:"🎟️", label:"Voucher Eligibility" },
    { id:"no-refund",     icon:"🚫", label:"No Refund Cases" },
  ];

  return (
    <LegalLayout
      title="Cancellation & Refund Policy"
      updated="March 5, 2021"
      badge="Cancellation Policy"
      intro="Understanding our cancellation and refund policy helps you plan your trips stress-free."
      tocItems={TOC}
    >
      <SEO page="cancellation" />
      {/* Quick summary cards */}
      <div id="overview" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:14, marginBottom:18, scrollMarginTop:"96px" }}>
        {[
          { icon:"✅", color:"#22c55e", bg:"rgba(34,197,94,.08)", border:"rgba(34,197,94,.25)", title:"Free Cancellation", desc:"Cancel for free before driver/car details are assigned." },
          { icon:"⚠️", color:"#f59e0b", bg:"rgba(245,158,11,.08)", border:"rgba(245,158,11,.25)", title:"After Driver Assigned",  desc:"Full advance amount forfeited if cancelled after driver details shared." },
          { icon:"🎟️", color:"#3b82f6", bg:"rgba(59,130,246,.08)", border:"rgba(59,130,246,.25)", title:"Vouchers Available", desc:"Lifetime vouchers issued in eligible cases like driver cancellation or breakdown." },
          { icon:"⏱️", color:"#8b5cf6", bg:"rgba(139,92,246,.08)", border:"rgba(139,92,246,.25)", title:"7–10 Business Days", desc:"Approved refunds reflect in your account within 7–10 business days." },
        ].map((c, i) => (
          <div key={i} style={{ background:c.bg, border:`1.5px solid ${c.border}`, borderRadius:16, padding:"18px 16px" }}>
            <div style={{ fontSize:28, marginBottom:10 }}>{c.icon}</div>
            <div style={{ fontWeight:800, fontSize:14, color:C.dark, marginBottom:6 }}>{c.title}</div>
            <div style={{ fontSize:12.5, color:C.gray, lineHeight:1.6 }}>{c.desc}</div>
          </div>
        ))}
      </div>

      <Section id="cancellation" icon="❌" title="1. Cancellation Policy">
        <Clause num="1.1" text={`You may cancel your booking at any time before the scheduled pickup by calling our 24×7 helpline: +91 92654 07669. Cancellation after driver/car details are shared will incur a cancellation fee.`} />
        <Clause num="1.2" text={`No cancellation fee is payable unless HaloSorath confirms the cancellation. Cancellations must be communicated directly — not through the driver.`} />
      </Section>

      <Section id="fees" icon="💰" title="1.3 Cancellation Fees">
        <div style={{ overflowX:"auto" }}>
          <table className="fee-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Cancellation Fee</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Driver / car details NOT yet provided</td>
                <td style={{ color:"#16a34a", fontWeight:700 }}>₹0 — FREE cancellation</td>
              </tr>
              <tr>
                <td>Driver / car details provided — any reason</td>
                <td style={{ color:"#dc2626", fontWeight:700 }}>Full advance amount forfeited</td>
              </tr>
              <tr>
                <td>Driver waited 45+ min at pickup (no-show / wrong address)</td>
                <td>Full advance forfeited OR waiting charges apply</td>
              </tr>
              <tr>
                <td>Waiting charge — Sedan / Hatchback (after 45 min)</td>
                <td>₹3.5 per minute</td>
              </tr>
              <tr>
                <td>Waiting charge — SUV / Innova (after 45 min)</td>
                <td>₹4 per minute</td>
              </tr>
              <tr>
                <td>Waiting charge — Tempo Traveller (after 45 min)</td>
                <td>₹10 per minute</td>
              </tr>
              <tr>
               
                <td style={{ color:"#2563eb", fontWeight:700 }}>Lifetime voucher for full advance</td>
              </tr>
            </tbody>
          </table>
        </div>
        
      </Section>

      <Section id="refund" icon="↩️" title="2. Refund Policy">
        <div className="box-blue">⏱️ Refunds are processed within <strong>7–10 business days</strong> after approval. HaloSorath is not responsible for delays caused by incorrect banking details provided by you.</div>
        <Clause num="2.1" text={`Refunds are processed as per the cancellation and refund policy. It may take 7 to 10 business days for the refund to reflect in your account. In case of wrong banking details, HaloSorath shall not be responsible for any loss.`} />
        <Clause num="2.3" text={`HaloSorath shall not be responsible to refund or provide a voucher in any case where you pay money or give goods to the Driver outside the confirmed invoice amount.`} />
        <Clause num="2.4" text={`HaloSorath shall not be held responsible for loss or damage during the ride caused by a driver who is a third-party agency. However, HaloSorath will assist you in all reasonable ways to recover your loss.`} />
        <Clause num="2.5" text={`HaloSorath reserves the right to withhold the complete advance amount on account of any misbehavior or misconduct (physical, verbal, or mental) toward the driver.`} />
      </Section>

      <Section id="vouchers" icon="🎟️" title="2.2 When You Are Eligible for Vouchers">
        <div className="box-green">✅ In the following situations, you are entitled to a <strong>no-expiry voucher</strong> for the complete advance amount paid.</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:14 }}>
          {[
            { num:"2.2.1", icon:"🚗", title:"Driver Cancels Last Minute",        desc:"The allocated driver cancels without valid reason at the last hour and HaloSorath cannot arrange an alternate ride in time." },
            { num:"2.2.2", icon:"🔧", title:"Vehicle Breakdown",                  desc:"The car breaks down during the trip and HaloSorath or the driver cannot rectify it within a reasonable time." },
          
            { num:"2.2.4", icon:"✏️", title:"Trip Modification Requested by You", desc:"You want to change car type, increase/decrease trip duration, or correct the itinerary — informed to HaloSorath well in advance and confirmed." },
          ].map((item, i) => (
            <div key={i} style={{ display:"flex", gap:14, alignItems:"flex-start", background:"rgba(34,197,94,.04)", border:"1px solid rgba(34,197,94,.2)", borderRadius:14, padding:"16px 16px" }}>
              <div style={{ width:42, height:42, borderRadius:12, background:"rgba(34,197,94,.1)", border:"1px solid rgba(34,197,94,.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{item.icon}</div>
              <div>
                <div style={{ fontWeight:700, fontSize:13.5, color:C.dark, marginBottom:3 }}>{item.num} — {item.title}</div>
                <div style={{ fontSize:13, color:C.gray, lineHeight:1.65 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="no-refund" icon="🚫" title="No Refund Cases">
        <div className="box-red">⚠️ <strong>No refund or voucher</strong> will be issued in the following circumstances.</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:14 }}>
          {[
            "You cancel after driver/car details have been provided (full advance forfeited)",
            "You shorten your trip, return early, or do not use all booked days/kilometers",
            "You paid money or gave goods to the driver outside the confirmed invoice",
            "You rode with a different driver or vehicle than the one assigned by HaloSorath",
            "You cancel based on vehicle fuel type, model, brand, or registration number preference",
            "You cancel on grounds of discrimination (religion, caste, gender, age)",
            "Driver waited 45+ minutes due to wrong address or you being unreachable",
            "You missed a train, flight, or event due to factors outside HaloSorath's control",
            "The 'Upgrade to Refundable Booking' add-on fee (non-refundable in all cases)",
            "You misbehave or are abusive toward the driver",
          ].map((item, i) => (
            <div key={i} className="clause">
              <span style={{ width:22, height:22, borderRadius:"50%", background:"rgba(239,68,68,.1)", border:"1px solid rgba(239,68,68,.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#dc2626", flexShrink:0, marginTop:1 }}>✕</span>
              <span>{item}</span>
            </div>
          ))}
        </div>

        <div style={{ marginTop:18, background:`linear-gradient(135deg,${C.gold},${C.goldD})`, borderRadius:16, padding:"18px 20px", display:"flex", gap:14, alignItems:"flex-start" }}>
          <div style={{ fontSize:28, flexShrink:0 }}>📞</div>
          <div>
            <div style={{ fontWeight:800, fontSize:14, color:C.dark, marginBottom:4 }}>Need to Cancel Your Booking?</div>
            <div style={{ fontSize:13, color:"rgba(26,26,46,.75)", marginBottom:10 }}>Call us immediately on our 24×7 helpline. Cancellation is only valid when confirmed by HaloSorath.</div>
            <a href="tel:+919265407669" style={{ textDecoration:"none" }}>
              <button style={{ background:C.dark, color:C.gold, border:"none", borderRadius:50, padding:"9px 22px", fontSize:13, fontWeight:700, fontFamily:F.body, cursor:"pointer" }}>+91 92654 07669 — Call Now</button>
            </a>
          </div>
        </div>
      </Section>
    </LegalLayout>
  );
}